import type { ReactNode } from "react";
import { CircleNotch } from "@phosphor-icons/react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type LoadingStateVariant = "skeleton" | "spinner" | "inline";

export interface LoadingStateProps {
  /** Visual treatment. See states-pattern.md §3 decision matrix. */
  variant: LoadingStateVariant;
  /** Number of skeleton rows to render. Only for variant="skeleton". Default: 3. */
  rows?: number;
  /** Accessible label for the status region. Default: "Carregando". */
  label?: string;
  /** Optional className for layout composition. */
  className?: string;
}

const SKELETON_WIDTHS = ["100%", "85%", "60%"] as const;

/**
 * Generic loading primitive. Renders a `role="status"` region with
 * `aria-live="polite"` and `aria-busy="true"`. Respects reduced motion via
 * the shared `motion-reduce:animate-none` utility pattern.
 */
export function LoadingState({
  variant,
  rows = 3,
  label = "Carregando",
  className,
}: LoadingStateProps): ReactNode {
  if (variant === "skeleton") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={label}
        data-state="loading"
        data-variant="skeleton"
        className={cn("w-full space-y-3", className)}
      >
        <span className="sr-only">{label}</span>
        {Array.from({ length: Math.max(1, rows) }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-6 motion-reduce:animate-none"
            style={{ width: SKELETON_WIDTHS[index % SKELETON_WIDTHS.length] }}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <span
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={label}
        data-state="loading"
        data-variant="inline"
        className={cn("inline-flex items-center gap-2 text-sm", className)}
      >
        <CircleNotch
          aria-hidden="true"
          className="h-4 w-4 animate-spin motion-reduce:animate-none"
          weight="bold"
        />
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  // variant === "spinner"
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
      data-state="loading"
      data-variant="spinner"
      className={cn("flex min-h-[120px] w-full items-center justify-center", className)}
    >
      <CircleNotch
        aria-hidden="true"
        className="h-8 w-8 animate-spin text-primary motion-reduce:animate-none"
        weight="bold"
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}
